import React, { FunctionComponent, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import SurveillanceServiceDataModel from '../../../../../data-models/SurveillanceService.DataModel';
import SurveillanceCaseDetailsEntity from '../../../../../entities/SurveillanceCaseDetails.Entity';
import styles from './SiuSurveillanceReferralForm.Styles';
import { SIU_SERVICE_TYPE_DESCRIPTIONS } from '../../referral-service/SiuServiceTypeDescriptions';
import referralServiceStyles from '../../referral-service/ReferralService.Styles';
import FacilityTypeEntity from '../../../../../entities/FacilityType.Entity';

const ADDITIONAL_COMMENTS_MAX = 2500;
const ALLEGED_INJURY_MAX = 250;
const RESTRICTIONS_MAX = 1000;
const SIU_REQUIRED_MESSAGE = 'This field is required';

const BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID = 64;
const EREMOTE_UNMANNED_SERVICE_TYPE_ID = 67;
const MEDICAL_CANVASS_SERVICE_TYPE_ID = 69;
const SURVEILLANCE_MANNED_SERVICE_TYPE_ID = 73;

const BACKGROUND_INVESTIGATION_OPTIONS = ['Standard', 'Traditional', 'Extended'];
const EREMOTE_OPTIONS = ['HD Live', 'HD Stream'];

const SERVICE_TYPE_LABELS: Record<number, string> = {
    [BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID]: 'Background Investigation',
    [EREMOTE_UNMANNED_SERVICE_TYPE_ID]: 'eRemote (Unmanned)',
    [MEDICAL_CANVASS_SERVICE_TYPE_ID]: 'Medical Canvass',
    [SURVEILLANCE_MANNED_SERVICE_TYPE_ID]: 'Surveillance (Manned)',
};

const SIU_NUMERIC_ALLOWED_CONTROL_KEYS = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Tab',
    'Home',
    'End',
    'Enter',
    'Escape',
];

const SIU_NUMERIC_BLOCKED_KEYS = ['e', 'E', '+', '-', '.', ','];

/** Matches ReferralServiceList SIU number inputs (creation flow). */
function siuNumericKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.ctrlKey || event.metaKey) {
        return;
    }
    if (SIU_NUMERIC_ALLOWED_CONTROL_KEYS.includes(event.key)) {
        return;
    }
    if (SIU_NUMERIC_BLOCKED_KEYS.includes(event.key) || !/^\d$/.test(event.key)) {
        event.preventDefault();
    }
}

/** Matches ReferralServiceList SIU number inputs (creation flow). */
function siuDigitsOnlyPaste(event: React.ClipboardEvent<HTMLInputElement>): void {
    const pastedText = event.clipboardData.getData('text');
    if (!/^\d+$/.test(pastedText)) {
        event.preventDefault();
    }
}

export interface ISiuSurveillanceReferralFormProps {
    referralId: number;
    serviceTypeIds: number[];
    facilityTypeCatalog: FacilityTypeEntity[];
    surveillanceService: SurveillanceServiceDataModel | null;
    onSurveillanceChange: (next: SurveillanceServiceDataModel) => void;
    openCloseStatus?: Record<string, boolean>;
    handleOpenCloseStatus?: (boxName: string, isOpened: boolean) => void;
    handleSetValidationFunction?: (currentPageValidationFunction: () => boolean) => void;
    showSiuNumberOfDaysError?: boolean;
    showSiuOptionError?: boolean;
    showSiuCaseDetailsError?: boolean;
}

function mergeCaseDetails(
    prev: SurveillanceServiceDataModel | null,
    patch: Partial<SurveillanceCaseDetailsEntity>,
): SurveillanceCaseDetailsEntity {
    const base = prev?.caseDetails ?? {};
    return { ...base, ...patch };
}

export const SiuSurveillanceReferralForm: FunctionComponent<ISiuSurveillanceReferralFormProps> = (props) => {
    const classes = styles();
    const listClasses = referralServiceStyles();
    const model = props.surveillanceService ?? {};

    const setModel = (patch: Partial<SurveillanceServiceDataModel>) => {
        const next: SurveillanceServiceDataModel = { ...model, ...patch };
        if (patch.caseDetails !== undefined) {
            next.caseDetails = { ...(model.caseDetails ?? {}), ...patch.caseDetails };
        }
        props.onSurveillanceChange(next);
    };

    const validateForm = (): boolean => {
        const cd = model.caseDetails ?? {};
        if (!cd.allegedInjury?.trim() || !cd.restrictions?.trim()) {
            return false;
        }
        const ids = props.serviceTypeIds ?? [];
        if (ids.includes(EREMOTE_UNMANNED_SERVICE_TYPE_ID)) {
            const d = model.eRemoteUnmannedNumberOfDays;
            if (d == null || d < 3) return false;
        }
        if (ids.includes(SURVEILLANCE_MANNED_SERVICE_TYPE_ID)) {
            const d = model.surveillanceMannedNumberOfDays;
            if (d == null || d < 1) return false;
        }
        if (ids.includes(MEDICAL_CANVASS_SERVICE_TYPE_ID)) {
            const fts = model.facilityTypes ?? [];
            if (fts.length === 0) return false;
            const n = model.medicalCanvassNumberOfFacilities;
            if (n == null || n < 10) return false;
        }
        if (ids.includes(BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID) && !model.backgroundInvestigationOption) {
            return false;
        }
        if (ids.includes(EREMOTE_UNMANNED_SERVICE_TYPE_ID) && !model.eRemoteUnmannedOption) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (props.handleOpenCloseStatus && props.openCloseStatus && !props.openCloseStatus.hasOwnProperty('SiuSurveillance_box')) {
            props.handleOpenCloseStatus('SiuSurveillance_box', true);
        }
    }, []);

    useEffect(() => {
        if (props.handleSetValidationFunction) {
            props.handleSetValidationFunction(validateForm);
        }
    });

    const handleOpenClose = (event: React.MouseEvent, boxName: string) => {
        event.persist();
        if (props.handleOpenCloseStatus && props.openCloseStatus) {
            props.handleOpenCloseStatus(boxName, !(props.openCloseStatus?.[boxName] ?? true));
        }
    };

    const caseDetails = model.caseDetails ?? {};
    const ids = props.serviceTypeIds ?? [];

    const showBi = ids.includes(BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID);
    const showERemote = ids.includes(EREMOTE_UNMANNED_SERVICE_TYPE_ID);
    const showMedicalCanvass = ids.includes(MEDICAL_CANVASS_SERVICE_TYPE_ID);
    const showSurveillanceManned = ids.includes(SURVEILLANCE_MANNED_SERVICE_TYPE_ID);

    const showNumberOfDaysError =
        props.showSiuNumberOfDaysError &&
        showERemote &&
        (model.eRemoteUnmannedNumberOfDays == null || model.eRemoteUnmannedNumberOfDays < 3);
    const showSurveillanceDaysError =
        props.showSiuNumberOfDaysError &&
        showSurveillanceManned &&
        (model.surveillanceMannedNumberOfDays == null || model.surveillanceMannedNumberOfDays < 1);
    const showMedicalFacilitiesError =
        props.showSiuNumberOfDaysError &&
        showMedicalCanvass &&
        (model.medicalCanvassNumberOfFacilities == null || model.medicalCanvassNumberOfFacilities < 10);
    const showMedicalFacilityTypesError =
        showMedicalCanvass &&
        (props.showSiuNumberOfDaysError || props.showSiuOptionError) &&
        !(model.facilityTypes && model.facilityTypes.length > 0);
    const showBiOptionError = props.showSiuOptionError && showBi && !model.backgroundInvestigationOption;
    const showERemoteOptionError = props.showSiuOptionError && showERemote && !model.eRemoteUnmannedOption;
    const showAllegedInjuryError = props.showSiuCaseDetailsError && !caseDetails.allegedInjury?.trim();
    const showRestrictionsError = props.showSiuCaseDetailsError && !caseDetails.restrictions?.trim();

    const renderServiceTypeBlock = (serviceTypeId: number, showDivider: boolean) => {
        const name = SERVICE_TYPE_LABELS[serviceTypeId] ?? 'Service';
        const descKey = name;
        const tooltip = SIU_SERVICE_TYPE_DESCRIPTIONS[descKey];

        return (
            <Grid
                item
                xs={12}
                key={serviceTypeId}
                className={`${classes.serviceTypeRow} ${showDivider ? classes.serviceTypeDivider : ''}`}
            >
                <Grid container spacing={2} alignItems="center" wrap="wrap">
                    <Grid item xs={12} sm={3} md={2} className={classes.serviceLabelCell}>
                        <Typography variant="subtitle2" className={classes.serviceLabelText} component="span">
                            {name}
                            {tooltip && (
                                <Tooltip
                                    title={tooltip}
                                    arrow
                                    placement="right"
                                    classes={{ tooltip: listClasses.siuTooltip, arrow: listClasses.siuTooltipArrow }}
                                >
                                    <InfoOutlined className={`${listClasses.siuInfoIcon} ${classes.serviceInfoIcon}`} />
                                </Tooltip>
                            )}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={9} md={10} className={classes.serviceControlsCell}>
                        <div className={classes.controlColumns}>
                            <div className={classes.ctrlColDropdown}>
                                {serviceTypeId === BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID && (
                                    <FormControl
                                        required
                                        size="small"
                                        error={showBiOptionError}
                                        className={`${classes.firstColumnControl} ${classes.inlineFormControl}`}
                                        fullWidth
                                    >
                                        <InputLabel id="siu-resubmit-bg-label">Select an option</InputLabel>
                                        <Select
                                            labelId="siu-resubmit-bg-label"
                                            fullWidth
                                            value={model.backgroundInvestigationOption ?? ''}
                                            onChange={(e) =>
                                                setModel({
                                                    backgroundInvestigationOption: (e.target.value as string) || undefined,
                                                })
                                            }
                                        >
                                            {BACKGROUND_INVESTIGATION_OPTIONS.map((opt) => (
                                                <MenuItem key={opt} value={opt}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                                {serviceTypeId === EREMOTE_UNMANNED_SERVICE_TYPE_ID && (
                                    <FormControl
                                        required
                                        size="small"
                                        error={showERemoteOptionError}
                                        className={`${classes.firstColumnControl} ${classes.inlineFormControl}`}
                                        fullWidth
                                    >
                                        <InputLabel id="siu-resubmit-er-label">Select an option</InputLabel>
                                        <Select
                                            labelId="siu-resubmit-er-label"
                                            fullWidth
                                            value={model.eRemoteUnmannedOption ?? ''}
                                            onChange={(e) =>
                                                setModel({
                                                    eRemoteUnmannedOption: (e.target.value as string) || undefined,
                                                })
                                            }
                                        >
                                            {EREMOTE_OPTIONS.map((opt) => (
                                                <MenuItem key={opt} value={opt}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                                {serviceTypeId === MEDICAL_CANVASS_SERVICE_TYPE_ID && (
                                    <FormControl
                                        required
                                        size="small"
                                        error={showMedicalFacilityTypesError}
                                        className={`${classes.firstColumnControl} ${classes.inlineFormControl}`}
                                        fullWidth
                                    >
                                        <InputLabel id="siu-resubmit-facility-types-label">Facility options</InputLabel>
                                        <Select
                                            labelId="siu-resubmit-facility-types-label"
                                            id="siu-resubmit-facility-types"
                                            fullWidth
                                            multiple
                                            label="Facility options"
                                            value={model.facilityTypes?.map((f) => f.facilityTypeId) ?? []}
                                            onChange={(e) => {
                                                const selectedIds = e.target.value as number[];
                                                const catalog = props.facilityTypeCatalog ?? [];
                                                const nextTypes = selectedIds.map((id) => {
                                                    const row = catalog.find((c) => c.facilityTypeId === id);
                                                    return {
                                                        facilityTypeId: id,
                                                        facilityTypeName: row?.facilityTypeName ?? String(id),
                                                    };
                                                });
                                                setModel({ facilityTypes: nextTypes });
                                            }}
                                            renderValue={(selected) => {
                                                const selectedArray = selected as number[];
                                                if (selectedArray.length === 0) {
                                                    return <em>Select facilities</em>;
                                                }
                                                if (selectedArray.length === 1) {
                                                    const facility = (props.facilityTypeCatalog ?? []).find(
                                                        (f) => f.facilityTypeId === selectedArray[0],
                                                    );
                                                    return facility?.facilityTypeName ?? '';
                                                }
                                                return `${selectedArray.length} facilities selected`;
                                            }}
                                            MenuProps={{
                                                variant: 'menu',
                                                getContentAnchorEl: null,
                                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                transformOrigin: { vertical: 'top', horizontal: 'left' },
                                            }}
                                        >
                                            {(props.facilityTypeCatalog ?? []).map((facilityType) => (
                                                <MenuItem
                                                    key={facilityType.facilityTypeId}
                                                    value={facilityType.facilityTypeId}
                                                    dense
                                                    className={listClasses.facilityTypeMenuItem}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        checked={
                                                            (model.facilityTypes?.map((f) => f.facilityTypeId) ?? []).indexOf(
                                                                facilityType.facilityTypeId,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={facilityType.facilityTypeName}
                                                        className={listClasses.facilityTypeItemText}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {showMedicalFacilityTypesError && <FormHelperText>Required</FormHelperText>}
                                    </FormControl>
                                )}
                                {serviceTypeId === SURVEILLANCE_MANNED_SERVICE_TYPE_ID && (
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="number"
                                        label="Number of days"
                                        required
                                        error={showSurveillanceDaysError}
                                        helperText={showSurveillanceDaysError ? 'Minimum 1' : ' '}
                                        FormHelperTextProps={{ style: { marginTop: 0, minHeight: 16 } }}
                                        className={`${classes.firstColumnControl} ${classes.narrowTextField} ${classes.inlineFormControl}`}
                                        fullWidth
                                        value={model.surveillanceMannedNumberOfDays ?? ''}
                                        onKeyDown={siuNumericKeyDown}
                                        onPaste={siuDigitsOnlyPaste}
                                        onChange={(e) => {
                                            const rawValue = e.target.value;
                                            if (rawValue === '') {
                                                setModel({ surveillanceMannedNumberOfDays: undefined });
                                                return;
                                            }
                                            if (!/^\d+$/.test(rawValue)) {
                                                return;
                                            }
                                            setModel({ surveillanceMannedNumberOfDays: parseInt(rawValue, 10) });
                                        }}
                                        InputProps={{ inputProps: { min: 1 } }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            </div>
                            <div className={classes.ctrlColNumeric}>
                                {serviceTypeId === EREMOTE_UNMANNED_SERVICE_TYPE_ID && (
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="number"
                                        label="Number of days"
                                        required
                                        error={showNumberOfDaysError}
                                        helperText={showNumberOfDaysError ? 'Minimum 3' : ' '}
                                        FormHelperTextProps={{ style: { marginTop: 0, minHeight: 16 } }}
                                        className={`${classes.narrowTextField} ${classes.inlineFormControl}`}
                                        fullWidth
                                        value={model.eRemoteUnmannedNumberOfDays ?? ''}
                                        onKeyDown={siuNumericKeyDown}
                                        onPaste={siuDigitsOnlyPaste}
                                        onChange={(e) => {
                                            const rawValue = e.target.value;
                                            if (rawValue === '') {
                                                setModel({ eRemoteUnmannedNumberOfDays: undefined });
                                                return;
                                            }
                                            if (!/^\d+$/.test(rawValue)) {
                                                return;
                                            }
                                            setModel({ eRemoteUnmannedNumberOfDays: parseInt(rawValue, 10) });
                                        }}
                                        InputProps={{ inputProps: { min: 3 } }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                                {serviceTypeId === MEDICAL_CANVASS_SERVICE_TYPE_ID && (
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="number"
                                        label="Number of facilities"
                                        required
                                        error={showMedicalFacilitiesError}
                                        helperText={showMedicalFacilitiesError ? 'Minimum 10' : ' '}
                                        FormHelperTextProps={{ style: { marginTop: 0, minHeight: 16 } }}
                                        className={`${classes.narrowTextField} ${classes.inlineFormControl}`}
                                        fullWidth
                                        value={model.medicalCanvassNumberOfFacilities ?? ''}
                                        onKeyDown={siuNumericKeyDown}
                                        onPaste={siuDigitsOnlyPaste}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const parsed = raw === '' ? undefined : Number.parseInt(raw, 10);
                                            setModel({
                                                medicalCanvassNumberOfFacilities: Number.isNaN(parsed as number)
                                                    ? undefined
                                                    : parsed,
                                            });
                                        }}
                                        InputProps={{ inputProps: { min: 10 } }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const orderedConfigurableTypes = [BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID, EREMOTE_UNMANNED_SERVICE_TYPE_ID, MEDICAL_CANVASS_SERVICE_TYPE_ID, SURVEILLANCE_MANNED_SERVICE_TYPE_ID].filter(
        (id) => ids.includes(id),
    );

    return (
        <Grid item xs={12}>
            <Accordion className={classes.paper} expanded={props.openCloseStatus?.['SiuSurveillance_box'] ?? true}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                        className={classes.paperHeader}
                        onClick={(event) => handleOpenClose(event, 'SiuSurveillance_box')}
                    >
                        <Typography variant="subtitle2" className={classes.sidebarTitle}>
                            Surveillance
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2} className={classes.gridContainer}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!caseDetails.rushRequest}
                                            onChange={(_, checked) =>
                                                setModel({
                                                    caseDetails: mergeCaseDetails(props.surveillanceService, { rushRequest: checked }),
                                                })
                                            }
                                            color="secondary"
                                        />
                                    }
                                    label={
                                        <span className={listClasses.radioButtonLabel}>
                                            This is a Rush Request
                                            <Tooltip
                                                title={
                                                    <>
                                                        RUSH: A highly time‑sensitive referral requiring priority handling.
                                                        <br />
                                                        • Field Investigations: Initiate within 2–3 business days and complete as
                                                        quickly as possible.
                                                        <br />
                                                        • Desktop Investigations: Complete within 1–2 business days.
                                                        <br />
                                                        Common drivers include upcoming hearings, court deadlines, or medical
                                                        appointments.
                                                    </>
                                                }
                                                arrow
                                                placement="right"
                                                classes={{ tooltip: listClasses.siuTooltip, arrow: listClasses.siuTooltipArrow }}
                                            >
                                                <InfoOutlined className={listClasses.siuInfoIcon} />
                                            </Tooltip>
                                        </span>
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Alleged Injury"
                                    required
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    variant="standard"
                                    value={caseDetails.allegedInjury ?? ''}
                                    onChange={(e) =>
                                        setModel({
                                            caseDetails: mergeCaseDetails(props.surveillanceService, {
                                                allegedInjury: e.target.value,
                                            }),
                                        })
                                    }
                                    error={showAllegedInjuryError}
                                    helperText={
                                        showAllegedInjuryError
                                            ? SIU_REQUIRED_MESSAGE
                                            : `${(caseDetails.allegedInjury ?? '').length} / ${ALLEGED_INJURY_MAX}`
                                    }
                                    inputProps={{ maxLength: ALLEGED_INJURY_MAX }}
                                    FormHelperTextProps={{ style: { marginTop: 4 } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Restrictions"
                                    required
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    variant="standard"
                                    value={caseDetails.restrictions ?? ''}
                                    onChange={(e) =>
                                        setModel({
                                            caseDetails: mergeCaseDetails(props.surveillanceService, {
                                                restrictions: e.target.value,
                                            }),
                                        })
                                    }
                                    error={showRestrictionsError}
                                    helperText={
                                        showRestrictionsError
                                            ? SIU_REQUIRED_MESSAGE
                                            : `${(caseDetails.restrictions ?? '').length} / ${RESTRICTIONS_MAX}`
                                    }
                                    inputProps={{ maxLength: RESTRICTIONS_MAX }}
                                    FormHelperTextProps={{ style: { marginTop: 4 } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Additional Comments"
                                    multiline
                                    minRows={1}
                                    fullWidth
                                    variant="standard"
                                    value={caseDetails.additionalInformation ?? ''}
                                    onChange={(e) =>
                                        setModel({
                                            caseDetails: mergeCaseDetails(props.surveillanceService, {
                                                additionalInformation: e.target.value,
                                            }),
                                        })
                                    }
                                    inputProps={{ maxLength: ADDITIONAL_COMMENTS_MAX }}
                                    helperText={`${(caseDetails.additionalInformation ?? '').length} / ${ADDITIONAL_COMMENTS_MAX}`}
                                    FormHelperTextProps={{ style: { marginTop: 4 } }}
                                />
                            </Grid>
                            {orderedConfigurableTypes.map((id, index) =>
                                renderServiceTypeBlock(id, index < orderedConfigurableTypes.length - 1),
                            )}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
        </Grid>
    );
};
